import { supabase } from "@/integrations/supabase/client";
import { type CanonicalIcd10Code, parseCanonicalIcd10Code } from "@smartmed/codes";
import type { AgeLimits, SexRestriction } from "@smartmed/diagnosis-rules";
import { useQuery } from "@tanstack/react-query";

export type CodeRestriction = AgeLimits & { genderRestriction: SexRestriction | null };

type ShownCode = { shown: string; canonical: CanonicalIcd10Code };

const parseShownCodes = (codes: readonly string[]): ShownCode[] =>
  codes.flatMap((shown) => {
    const canonical = parseCanonicalIcd10Code(shown);
    return canonical ? [{ shown, canonical }] : [];
  });

const byShownCode = (shownCodes: readonly ShownCode[]) => (restrictions: ReadonlyMap<string, CodeRestriction>) =>
  new Map(
    shownCodes.flatMap(({ shown, canonical }) => {
      const restriction = restrictions.get(canonical);
      return restriction ? [[shown, restriction] as const] : [];
    }),
  );

const hasRestriction = ({ genderRestriction, minAge, maxAge }: CodeRestriction): boolean =>
  genderRestriction !== null || minAge !== null || maxAge !== null;

/** On-screen ICD-10 codes with a sex restriction or an age limit, keyed by the code as shown; looked up by its canonical form. */
export const useCodeRestrictions = (codes: readonly string[]) => {
  const shownCodes = parseShownCodes(codes);
  const canonicalCodes = [...new Set(shownCodes.map(({ canonical }) => canonical))].sort();

  return useQuery({
    queryKey: ["code-restrictions", canonicalCodes],
    enabled: canonicalCodes.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("icd10_codes")
        .select("code, gender_restriction, min_age, max_age")
        .in("code", canonicalCodes);
      if (error) throw error;

      return new Map<string, CodeRestriction>(
        (data ?? []).flatMap((row) => {
          const restriction: CodeRestriction = {
            genderRestriction: row.gender_restriction,
            minAge: row.min_age,
            maxAge: row.max_age,
          };
          return hasRestriction(restriction) ? [[row.code, restriction] as const] : [];
        }),
      );
    },
    select: byShownCode(shownCodes),
  });
};
